import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Modal,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { insuranceAgentInfoService } from '../../../../services/insuranceAgentInfo';
import { Alert, ActivityIndicator } from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('InsuranceAgentInfoInput');

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export const InsuranceAgentInfoInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { insuranceAgentId?: string; isEdit?: boolean }>, string>>();
  const [showStateModal, setShowStateModal] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const insuranceAgentId = (route.params as any)?.insuranceAgentId as string | undefined;
  const isEdit = !!(route.params as any)?.isEdit;

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  // Helper: parse concatenated address back into components
  // Address format: "123 Main St Dallas, TX 75201"
  const parseAddress = (officeAddress: string) => {
    if (!officeAddress) {
      return { street: '', city: '', state: '', zip: '' };
    }

    let street = '';
    let city = '';
    let state = '';
    let zip = '';
    
    // Try to extract zip (5 digits at the end)
    const zipMatch = officeAddress.match(/\b(\d{5})\s*$/);
    if (zipMatch) {
      zip = zipMatch[1];
      const remaining = officeAddress.replace(/\b\d{5}\s*$/, '').trim();
      
      // Try to extract state (2 letters before zip, potentially after comma)
      const stateMatch = remaining.match(/,?\s*([A-Z]{2})\s*$/);
      if (stateMatch) {
        state = stateMatch[1];
        const streetAndCity = remaining.replace(/,?\s*[A-Z]{2}\s*$/, '').trim();
        
        // Find the last comma to separate city from street
        const lastCommaIndex = streetAndCity.lastIndexOf(',');
        if (lastCommaIndex > -1) {
          street = streetAndCity.substring(0, lastCommaIndex).trim();
          city = streetAndCity.substring(lastCommaIndex + 1).trim();
        } else {
          // No comma found, try to split by looking for common street patterns
          const parts = streetAndCity.split(' ');
          if (parts.length > 1) {
            if (parts.length <= 3) {
              street = parts.slice(0, -1).join(' ');
              city = parts[parts.length - 1];
            } else {
              street = parts.slice(0, -2).join(' ');
              city = parts.slice(-2).join(' ');
            }
          } else {
            street = streetAndCity;
          }
        }
      } else {
        street = remaining;
      }
    } else {
      // No zip found, try to parse what we can
      const commaIndex = officeAddress.lastIndexOf(',');
      if (commaIndex > -1) {
        street = officeAddress.substring(0, commaIndex).trim();
        const afterComma = officeAddress.substring(commaIndex + 1).trim();
        
        const stateMatch = afterComma.match(/^([A-Z]{2})$/);
        if (stateMatch) {
          state = stateMatch[1];
        } else {
          city = afterComma;
        }
      } else {
        street = officeAddress;
      }
    }
    
    return { street, city, state, zip };
  };

  const [formData, setFormData] = useState({
    agentName: '',
    agencyName: '',
    officePhone: '',
    cellPhone: '',
    email: '',
    website: '',
    username: '',
    password: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    specialInstructions: '',
    // Products handled
    auto: false,
    home: false,
    boat: false,
    motorcycle: false,
    jetskiWheeler: false,
    umbrellaPolicy: false,
    lifeInsurance: false,
    healthInsurance: false,
    other: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [savedInsuranceAgentId, setSavedInsuranceAgentId] = useState<string | null>(null);

  useEffect(() => {
    if (isEdit && insuranceAgentId) {
      (async () => {
        try {
          // Load existing insurance agent record
          const list = await insuranceAgentInfoService.getAllInsuranceAgentInfo();
          const record = list.find((a: any) => a.id === insuranceAgentId);
          if (record) {
            // Parse the address back into components
            const addressParts = parseAddress(record.officeAddress || '');
            logger.debug('[Address Debug] Original officeAddress:', record.officeAddress);
            logger.debug('[Address Debug] Parsed address parts:', addressParts);

            // Parse insurance types from comma-separated string
            const insuranceTypes = (record.insuranceType || '').split(',').map((type: string) => type.trim());
            
            setFormData(prev => ({
              ...prev,
              agentName: record.agentName || '',
              agencyName: record.agencyName || '',
              officePhone: formatPhoneNumber(record.officePhone || ''),
              cellPhone: formatPhoneNumber(record.cellPhone || ''),
              email: record.email || '',
              website: record.website || '',
              street: addressParts.street,
              city: addressParts.city,
              state: addressParts.state,
              zip: addressParts.zip,
              specialInstructions: record.notes || '',
              // Map insurance types to checkboxes
              auto: insuranceTypes.includes('Auto'),
              home: insuranceTypes.includes('Home'),
              boat: insuranceTypes.includes('Boat'),
              motorcycle: insuranceTypes.includes('Motorcycle'),
              jetskiWheeler: insuranceTypes.includes('Jetski/Wheeler'),
              umbrellaPolicy: insuranceTypes.includes('Umbrella Policy'),
              lifeInsurance: insuranceTypes.includes('Life Insurance'),
              healthInsurance: insuranceTypes.includes('Health Insurance'),
              other: insuranceTypes.includes('Other'),
            }));
            // Try to load portal credentials
            try {
              const creds = await vaultService.getPortalCredentials(insuranceAgentId, 'insurance-agent');
              if (creds && (creds.username || creds.password)) {
                setHasOnlinePortal(true);
                setFormData(prev => ({
                  ...prev,
                  username: creds.username || '',
                  password: creds.password || '',
                }));
              }
            } catch {}
          }
        } catch (e) {
          logger.debug('Failed to load insurance agent for edit', e);
        }
      })();
    }
  }, [isEdit, insuranceAgentId]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((formData.username || formData.password) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }
      // Build backend payload
      const officeAddress = [
        formData.street?.trim(),
        [formData.city?.trim(), formData.state?.trim()].filter(Boolean).join(', '),
        formData.zip?.trim(),
      ]
        .filter(Boolean)
        .join(' ');

      // Build insurance types string from checkboxes
      const insuranceTypes = [];
      if (formData.auto) insuranceTypes.push('Auto');
      if (formData.home) insuranceTypes.push('Home');
      if (formData.boat) insuranceTypes.push('Boat');
      if (formData.motorcycle) insuranceTypes.push('Motorcycle');
      if (formData.jetskiWheeler) insuranceTypes.push('Jetski/Wheeler');
      if (formData.umbrellaPolicy) insuranceTypes.push('Umbrella Policy');
      if (formData.lifeInsurance) insuranceTypes.push('Life Insurance');
      if (formData.healthInsurance) insuranceTypes.push('Health Insurance');
      if (formData.other) insuranceTypes.push('Other');

      const payload: any = {
        agencyName: formData.agencyName || undefined,
        agentName: formData.agentName || undefined,
        insuranceType: insuranceTypes.join(', ') || undefined,
        officePhone: formData.officePhone.replace(/\D/g, ''),
        cellPhone: formData.cellPhone.replace(/\D/g, ''),
        email: formData.email.toLowerCase() || undefined,
        website: formData.website || undefined,
        officeAddress: officeAddress || undefined,
        notes: formData.specialInstructions || undefined,
      };

      // Create or update insurance agent record
      let savedId = insuranceAgentId as string | undefined;
      if (isEdit && insuranceAgentId) {
        const updated = await insuranceAgentInfoService.updateInsuranceAgentInfo(insuranceAgentId, payload);
        // Handle wrapped response format { success, data, message }
        const updatedData = updated?.data || updated;
        savedId = updatedData.id;
      } else {
        const saved = await insuranceAgentInfoService.createInsuranceAgentInfo(payload);
        // Handle wrapped response format { success, data, message }
        const createdData = saved?.data || saved;
        savedId = createdData.id;
      }

      // Store the saved ID for the Done button
      setSavedInsuranceAgentId(savedId);

      // Store portal credentials in Vault if provided
      if (savedId && (formData.username || formData.password)) {
        try {
          logger.debug('[Portal Debug] Storing portal credentials in vault for insurance agent ID:', savedId);
          logger.debug('[Portal Debug] Username provided:', !!formData.username);
          logger.debug('[Portal Debug] Password provided:', !!formData.password);
          await vaultService.storePortalCredentials(savedId, 'insurance-agent', {
            username: formData.username,
            password: formData.password,
          });
          logger.debug('[Portal Debug] Successfully stored portal credentials in vault');
        } catch (e) {
          logger.error('[Portal Debug] Vault write failed for insurance agent portal credentials:', e);
        }
      }

      // Show Add Another / Done buttons after successful save
      setShowAddAnotherDone(true);
    } catch (error: any) {
      logger.error('Error saving insurance agent info:', error);
      Alert.alert('Error', 'Failed to save insurance agent information. Please try again.');
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setFormData({
      agentName: '',
      agencyName: '',
      officePhone: '',
      cellPhone: '',
      email: '',
      website: '',
      username: '',
      password: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      specialInstructions: '',
      auto: false,
      home: false,
      boat: false,
      motorcycle: false,
      jetskiWheeler: false,
      umbrellaPolicy: false,
      lifeInsurance: false,
      healthInsurance: false,
      other: false,
    });
    setHasOnlinePortal(false);
    setShowAddAnotherDone(false);
    setSubmitting(false);
  };

  const handleDone = () => {
    setSubmitting(false);
    // Navigate to lock/unlock with the saved ID
    const idToUse = savedInsuranceAgentId || insuranceAgentId;
    logger.debug('[Done Debug] Using ID:', idToUse, 'savedInsuranceAgentId:', savedInsuranceAgentId, 'insuranceAgentId:', insuranceAgentId);
    (navigation as any).navigate('InsuranceAgentInfoLockUnlock', { insuranceAgentId: idToUse });
  };

  const handleStateSelect = (state: string) => {
    setFormData({ ...formData, state });
    setShowStateModal(false);
  };

  const toggleCheckbox = (field: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field as keyof typeof prev]
    }));
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>{isEdit ? 'Edit Insurance Agent Info' : 'Add Insurance Agent Info'}</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Agent Name</Text>
            <TextInput
              style={styles.input}
              value={formData.agentName}
              onChangeText={(text) => setFormData({ ...formData, agentName: text })}
              placeholder="Enter Agent Name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Agency Name</Text>
            <TextInput
              style={styles.input}
              value={formData.agencyName}
              onChangeText={(text) => setFormData({ ...formData, agencyName: text })}
              placeholder="Enter Agency Name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Office Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.officePhone}
              onChangeText={(text) => setFormData({ ...formData, officePhone: formatPhoneNumber(text) })}
              placeholder="Enter Office Phone"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Cell Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.cellPhone}
              onChangeText={(text) => setFormData({ ...formData, cellPhone: formatPhoneNumber(text) })}
              placeholder="Enter Cell Phone"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email</Text>
            <TextInput
              style={styles.input}
              value={formData.email}
              onChangeText={(text) => setFormData({ ...formData, email: text.toLowerCase() })}
              placeholder="Enter Email"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Website</Text>
            <TextInput
              style={styles.input}
              value={formData.website}
              onChangeText={(text) => setFormData({ ...formData, website: text })}
              placeholder="Enter Website"
              placeholderTextColor="#A9A9A9"
              keyboardType="url"
              autoCapitalize="none"
            />
          </View>

          {/* Portal Toggle */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
            >
              <View style={[styles.toggle, hasOnlinePortal && styles.toggleActive]}>
                <View style={[styles.toggleThumb, hasOnlinePortal && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Does your insurance agent have an online portal?</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>
              If yes, enter your login credentials. These will be securely stored in your vault and will only be shared with your successor after your End of Life event.
            </Text>
          </View>

          {hasOnlinePortal && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showUsername}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Street Address</Text>
            <TextInput
              style={styles.input}
              value={formData.street}
              onChangeText={(text) => setFormData({ ...formData, street: text })}
              placeholder="Enter Street Address"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>City</Text>
            <TextInput
              style={styles.input}
              value={formData.city}
              onChangeText={(text) => setFormData({ ...formData, city: text })}
              placeholder="Enter City"
              placeholderTextColor="#A9A9A9"
              autoCapitalize="words"
            />
          </View>

          <View style={styles.rowContainer}>
            <View style={[styles.inputGroup, { flex: 1, marginRight: 10 }]}>
              <Text style={styles.label}>State</Text>
              <TouchableOpacity
                style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
                onPress={() => setShowStateModal(true)}
              >
                <Text style={[
                  { fontSize: 16, color: '#1C2C4F' },
                  !formData.state && { color: '#A9A9A9' }
                ]}>
                  {formData.state || 'Select State'}
                </Text>
                <Image
                  source={require('../../../../assets/icons/down.png')}
                  style={[styles.dropdownIcon, { tintColor: '#1C2C4F' }]}
                />
              </TouchableOpacity>
            </View>

            <View style={[styles.inputGroup, { flex: 1 }]}>
              <Text style={styles.label}>Zip</Text>
              <TextInput
                style={styles.input}
                value={formData.zip}
                onChangeText={(text) => setFormData({ ...formData, zip: text })}
                placeholder="Enter Zip"
                placeholderTextColor="#A9A9A9"
                keyboardType="number-pad"
                maxLength={5}
              />
            </View>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Special Instructions</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.specialInstructions}
              onChangeText={(text) => setFormData({ ...formData, specialInstructions: text })}
              placeholder="Enter any special instructions"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          {/* Products Section */}
          <View style={styles.sectionContainer}>
            <Text style={styles.sectionTitle}>Products handled by your insurance company:</Text>
            <View style={styles.checkboxContainer}>
              <TouchableOpacity
                style={styles.checkboxRow}
                onPress={() => toggleCheckbox('auto')}
              >
                <View style={[styles.checkbox, formData.auto && styles.checkboxChecked]}>
                  {formData.auto && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkboxLabel}>Auto</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.checkboxRow}
                onPress={() => toggleCheckbox('home')}
              >
                <View style={[styles.checkbox, formData.home && styles.checkboxChecked]}>
                  {formData.home && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkboxLabel}>Home</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.checkboxRow}
                onPress={() => toggleCheckbox('boat')}
              >
                <View style={[styles.checkbox, formData.boat && styles.checkboxChecked]}>
                  {formData.boat && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkboxLabel}>Boat</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.checkboxRow}
                onPress={() => toggleCheckbox('motorcycle')}
              >
                <View style={[styles.checkbox, formData.motorcycle && styles.checkboxChecked]}>
                  {formData.motorcycle && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkboxLabel}>Motorcycle</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.checkboxRow}
                onPress={() => toggleCheckbox('jetskiWheeler')}
              >
                <View style={[styles.checkbox, formData.jetskiWheeler && styles.checkboxChecked]}>
                  {formData.jetskiWheeler && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkboxLabel}>Jetski/4-Wheeler</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.checkboxRow}
                onPress={() => toggleCheckbox('umbrellaPolicy')}
              >
                <View style={[styles.checkbox, formData.umbrellaPolicy && styles.checkboxChecked]}>
                  {formData.umbrellaPolicy && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkboxLabel}>Umbrella Policy</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.checkboxRow}
                onPress={() => toggleCheckbox('lifeInsurance')}
              >
                <View style={[styles.checkbox, formData.lifeInsurance && styles.checkboxChecked]}>
                  {formData.lifeInsurance && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkboxLabel}>Life Insurance</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.checkboxRow}
                onPress={() => toggleCheckbox('healthInsurance')}
              >
                <View style={[styles.checkbox, formData.healthInsurance && styles.checkboxChecked]}>
                  {formData.healthInsurance && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkboxLabel}>Health Insurance</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.checkboxRow}
                onPress={() => toggleCheckbox('other')}
              >
                <View style={[styles.checkbox, formData.other && styles.checkboxChecked]}>
                  {formData.other && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkboxLabel}>Other</Text>
              </TouchableOpacity>
            </View>
          </View>

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity
                style={[styles.submitButton, styles.addAnotherButton]}
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.submitButton, styles.doneButton]}
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {submitting ? (
                <ActivityIndicator size="large" color="#29BFC0" />
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSubmit}
                >
                  <Text style={styles.submitButtonText}>{isEdit ? 'Update' : 'Submit'}</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <Modal
        visible={showStateModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowStateModal(false)}
        statusBarTranslucent
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowStateModal(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select State</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowStateModal(false)}
              >
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {US_STATES.map((state) => (
                <TouchableOpacity
                  key={state}
                  style={styles.stateItem}
                  onPress={() => handleStateSelect(state)}
                >
                  <Text style={styles.stateText}>{state}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
      position: 'absolute',
      width: '85%',
      height: '85%',
      opacity: 0.1,
    },
  logo: {
    width: s(125),
    height: s(125),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginTop: ms(10),
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    padding: ms(20),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(20),
  },
  rowContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
  },
  sectionContainer: {
    marginTop: ms(30),
    marginBottom: ms(20),
  },
  sectionTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(15),
  },
  checkboxContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    borderWidth: ms(1),
    borderColor: '#E5E7EB',
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(15),
  },
  checkbox: {
    width: s(24),
    height: s(24),
    borderWidth: ms(2),
    borderColor: '#1C2C4F',
    borderRadius: ms(4),
    marginRight: ms(10),
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#1C2C4F',
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
  },
  checkboxLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
    gap: ms(20),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggle: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#E5E7EB',
    marginRight: ms(15),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
  },
  toggleThumb: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
  passwordContainer: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: ms(50),
  },
  eyeButton: {
    position: 'absolute',
    right: ms(15),
    top: ms(15),
    padding: ms(5),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(20),
    width: '85%',
    maxHeight: '70%',
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(20),
    borderBottomWidth: ms(1),
    borderBottomColor: '#E5E7EB',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    width: s(30),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalCloseText: {
    fontSize: ms(18, 0.3),
    color: '#666',
    fontWeight: '600',
  },
  stateList: {
    padding: ms(20),
  },
  stateItem: {
    paddingVertical: ms(15),
    borderBottomWidth: ms(1),
    borderBottomColor: '#E5E7EB',
  },
  stateText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    textAlign: 'center',
  },
});